import Ember from 'ember';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence } from 'ember-changeset-validations/validators';
import { timeout } from 'ember-concurrency';
import { restartableTask } from 'ember-concurrency';
import moment from 'moment-timezone';
import { BufferedChangeset } from 'validated-changeset';
import Simulation, { ResourceName } from 'volta/models/simulation';
import SimulationScenario from 'volta/models/simulation-scenario';
import SkuModel from 'volta/models/sku';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { distinctBy } from 'volta/utils/array-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { eqFilter, inFilter, lkFilter } from 'volta/utils/filters-utils';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type BvFlashService from 'volta/services/bv-flash';
import type IntlService from 'ember-intl/services/intl';
import type LabelService from 'volta/services/label-service';
import type StoreService from 'volta/services/-store';
import type { DocWithData, IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type { ICreateSimulationCmd, IDuplicateSimulationCmd } from 'volta/models/types/simulation';
import { ModelRegistry } from "ember-data/model";
import SkusCollectionDefinitionBuilder from 'volta/services/collection-builder-skus';

interface IArgs {
  name?: string;
  startDate?: Date;
  endDate?: Date;
  skuIds?: string[];
  onCreateSuccess?: (simu: Simulation) => void;
  simulationToDuplicate?: Simulation;
  forecastTypes?: string[];
}

const CreateValidations = {
  name: [validatePresence(true)],
  startDate: [validatePresence(true)],
  endDate: [validatePresence(true)],
  skuIds: [validatePresence(true)],
  forecastTypes: []
};

const DuplicateValidations = {
  name: [validatePresence(true)],
  startDate: [validatePresence(true)],
  endDate: [validatePresence(true)],
  simulationId: [validatePresence(true)],
  forecastTypes: [],
  excludedScenarioIds: []
};

const PAGE_SIZE = 50;
const DEBOUNCE_MS = 250;

export default class NewSimulation extends Component<IArgs> {
  // Services
  // ~~~~

  @service intl!: IntlService;
  @service store!: StoreService;
  @service bvFlash!: BvFlashService;
  @service labelService!: LabelService;
  @service collectionBuilderSkus!: SkusCollectionDefinitionBuilder;

  // Properties
  // ~~~~

  resourceName = ResourceName;
  @tracked newSimulationCmd?: ICreateSimulationCmd | IDuplicateSimulationCmd;
  @tracked changeset!: BufferedChangeset;
  @tracked skusSortColumn = 'part.code';
  @tracked skusSortAscending = true;
  @tracked isLoading = false;
  @tracked backendErrors?: Record<string, string[] | null> = {};

  @tracked skus: SkuModel[] = [];
  @tracked selection: SkuModel[] = [];
  @tracked searchSelection: SkuModel[] = [];
  @tracked sorts = [{ valuePath: 'part.code', isAscending: true }];
  @tracked searchText: string = '';

  // Computed Properties
  // ~~~~

  @sort('skus', 'skusSortProperties')
  sortedSkus!: SkuModel[];

  get isFromScratch() {
    const { skuIds } = this.args;
    return (!skuIds || skuIds.length === 0) && !this.args.simulationToDuplicate;
  }

  get skuIds() {
    return this.skus.map((s) => s.id);
  }

  get warehouseCount() {
    return this.isFromScratch
      ? new Set(this.skus.map((s) => s.warehouse.warehouseId)).size
      : undefined;
  }

  get skusSortProperties() {
    return this.sorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  @cached
  get columns() {
    return this.collectionBuilderSkus.buildColumns();
  }

  get scenariosToDuplicate() {
    if (!this.args.simulationToDuplicate) {
      return [];
    }
    const excludedScenarios: string[] = this.changeset.get('excludedScenarioIds') ?? [];
    return this.args.simulationToDuplicate?.actualScenarios.filter((scenario) => {
      return !excludedScenarios.includes(scenario.id);
    });
  }

  // Lifecycle Hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.initChangeset();
  }

  // Actions
  // ~~~~

  @action
  async addItems(skus: SkuModel[]) {
    if (skus.length) {
      this.skus = distinctBy(this.skus.concat(skus), 'id');
      this.changeset.set('skuIds', this.skuIds);
      await this.validate();
      this.searchSelection = [];
      this.fetchSkus.perform();
    }
  }

  @action
  async onRemoveItems(skus: SkuModel[]) {
    const idsToRemove = new Set(skus.map((s) => s.id));
    if (skus.length) {
      this.skus = this.skus.filter((s) => !idsToRemove.has(s.id));
      this.changeset.set('skuIds', this.skuIds);
      await this.validate();
      this.fetchSkus.perform();
    }
  }

  @action
  async validate() {
    await this.changeset.validate();
  }

  @action
  async resetSkus() {
    this.skus = [];
    this.searchSelection = [];
    this.changeset.set('skuIds', this.skuIds);
    await this.validate();
    this.fetchSkus.perform();
  }

  @action
  onSearchChange(value: string) {
    this.searchText = value;
    this.fetchSkus.perform();
  }

  @action
  handleChangeDuplicatedScenarios(scenarios: SimulationScenario[]) {
    const actualScenarios = (this.args.simulationToDuplicate?.actualScenarios ?? []) as SimulationScenario[];
    const selectedIds = scenarios.mapBy('id');
    const excludedScenarios = actualScenarios.filter((sc) => {
      return !selectedIds.includes(sc.id);
    });
    this.changeset.set(
      'excludedScenarioIds',
      excludedScenarios?.map((sc) => sc.id)
    );
  }

  // Tasks
  // ~~~~~

  fetchSkus = restartableTask(async (apiQuery?: IJsonApiQuery) => {
    const skuIds = this.changeset.get('skuIds');
    const duplicateId = this.args.simulationToDuplicate?.id as string | undefined;
    try {
      if (!this.isFromScratch) {
        if (!skuIds?.length && !duplicateId) {
          this.skus = [];
        } else {
          if (!apiQuery) {
            apiQuery = {
              sort: 'part.code',
              page: {
                limit: PAGE_SIZE,
                offset: 0
              }
            };
          }
          apiQuery.filter = apiQuery.filter ?? {};

          if (duplicateId) {
            apiQuery.filter['simulationId'] = eqFilter(duplicateId);
          } else {
            apiQuery.filter['id'] = inFilter(skuIds);
          }
          if (this.searchText.length) {
            apiQuery.filter.q = lkFilter(this.searchText);
          }

          await timeout(DEBOUNCE_MS);
          const result = (await this.store.normalPost('skus/list', apiQuery)) as DocWithData;
          const skus = normalize('sku' as keyof ModelRegistry, result) as Ember.Array<SkuModel>;
          this.skus = skus.toArray();
          this.isLoading = false;
          // @ts-ignore
          skus.meta = result.meta;
          return skus;
        }
      }
      return this.searchText.length
        ? this.skus.filter((s) => s.validateSearch(this.searchText))
        : this.skus;
    } catch (error) {
      this.bvFlash.apiError(error);
      this.isLoading = false;
      return defaultErrorHandler(error);
    }
  });

  saveSimulation = restartableTask(async () => {
    const newSimu = this.changeset;
    await newSimu.validate();

    if (isPresent(newSimu) && newSimu.isValid) {
      const cmd: ICreateSimulationCmd = {
        name: newSimu.get('name'),
        description: newSimu.get('description'),
        startDate: moment(newSimu.get('startDate')).format('YYYY-MM-DD'),
        endDate: moment(newSimu.get('endDate')).format('YYYY-MM-DD'),
        skuIds: newSimu.get('skuIds'),
        forecastTypes: newSimu.get('forecastTypes')
      };

      try {
        const simulation = (await Simulation.createSimulation(cmd)) as Simulation;
        this.isLoading = false;
        this.backendErrors = {};

        this.bvFlash.success(
          this.intl.t('bvList.singleActions.createSuccess', {
            resourceNameSingular: this.intl.t(ResourceName.singular).toLowerCase(),
            name: simulation.name
          })
        );
        this.args.onCreateSuccess?.(simulation);
        return simulation;
      } catch (error) {
        defaultErrorHandler(error);
        this.isLoading = false;
        const backendErrors = this.labelService.formValidationsFromApiError(error);
        if (backendErrors) {
          this.backendErrors = backendErrors;
        }
        this.bvFlash.error(
          this.intl.t('bvList.singleActions.createFail', {
            resourceNameSingular: this.intl.t(ResourceName.singular).toLowerCase(),
            name: this.changeset.name
          })
        );
        return error;
      }
    }
  });

  duplicateSimulation = restartableTask(async () => {
    const newSimu = this.changeset;
    await newSimu.validate();

    const startDate = newSimu.get('startDate');
    const endDate = newSimu.get('endDate');

    if (isPresent(newSimu) && newSimu.isValid) {
      const cmd: IDuplicateSimulationCmd = {
        simulationId: newSimu.get('simulationId'),
        name: newSimu.get('name'),
        description: newSimu.get('description'),
        startDate: moment.min(startDate, endDate).format('YYYY-MM-DD'),
        endDate: moment.max(startDate, endDate).format('YYYY-MM-DD'),
        excludedScenarioIds: newSimu.get('excludedScenarioIds'),
        forecastTypes: newSimu.get('forecastTypes')
      };

      try {
        const simulation = (await Simulation.duplicateSimulation(cmd)) as Simulation;
        this.isLoading = false;
        this.backendErrors = {};

        this.bvFlash.success(
          this.intl.t('bvList.singleActions.createSuccess', {
            resourceNameSingular: this.intl.t(ResourceName.singular).toLowerCase(),
            name: simulation.name
          })
        );
        this.args.onCreateSuccess?.(simulation);
        return simulation;
      } catch (error) {
        defaultErrorHandler(error);
        this.isLoading = false;
        const backendErrors = this.labelService.formValidationsFromApiError(error);
        if (backendErrors) {
          this.backendErrors = backendErrors;
        }
        this.bvFlash.error(
          this.intl.t('bvList.singleActions.createFail', {
            resourceNameSingular: this.intl.t(ResourceName.singular).toLowerCase(),
            name: this.changeset.name
          })
        );
        return error;
      }
    }
  });

  // Private Helpers
  // ~~~~~

  private initChangeset() {
    const {
      skuIds = [],
      startDate,
      endDate,
      name,
      forecastTypes,
      simulationToDuplicate
    } = this.args;

    if (simulationToDuplicate) {
      this.newSimulationCmd = {
        simulationId: simulationToDuplicate.id,
        excludedScenarioIds: [],
        startDate: simulationToDuplicate.startDate,
        endDate: simulationToDuplicate.endDate,
        name: simulationToDuplicate.name,
        forecastTypes: simulationToDuplicate.forecastTypes
      };
    } else {
      const initialStartDate = (
        startDate ? moment(startDate) : moment().subtract(1, 'year')
      ).toDate();
      const initialEndDate = (
        endDate && moment(endDate).isAfter(initialStartDate)
          ? moment(endDate)
          : moment(initialStartDate).add(1, 'year')
      ).toDate();
      this.newSimulationCmd = {
        skuIds,
        startDate: initialStartDate,
        endDate: initialEndDate,
        name: name ?? '',
        forecastTypes
      };
    }

    const Validations = simulationToDuplicate ? DuplicateValidations : CreateValidations;
    this.changeset = Changeset(this.newSimulationCmd, lookupValidator(Validations), Validations);
  }
}
